import {
    type B2bDispatch,
    type B2bOrderDetail,
    cancelB2bDispatch,
    closeB2bOrderShort,
    createB2bDispatch,
    type DispatchDocument,
    type DispatchForm,
    getB2bConfig,
    getB2bOrderFileUrl,
    getB2bPackingListUrl,
    recordB2bDispatch,
} from '@api/b2b';
import { listWarehousesForPicker } from '@api/work-order';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, FileInput, Modal, NumberInput, Select, TextInput, Textarea } from '@mantine/core';
import { FileXlsIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import styles from './b2b.module.css';
import { Card, Field, Pill } from './shared';
import { errorMessage, formatDay, openSignedFile, todayIst, units } from './utils';


const DOC_KINDS: Array<{ value: DispatchDocument['kind']; label: string }> = [
    { value: 'TAX_INVOICE', label: 'Tax invoice' },
    { value: 'LR_COPY', label: 'LR / AWB copy' },
    { value: 'EWAY_BILL', label: 'E-way bill' },
    { value: 'PACKING_LIST', label: 'Packing list' },
    { value: 'OTHER', label: 'Other' },
];

const RecordDispatchModal: React.FC<{
    dispatch: B2bDispatch;
    onClose: () => void;
    onSubmit: (form: DispatchForm, files: Array<{ kind: DispatchDocument['kind']; file: File }>) => Promise<void>;
    busy: boolean;
    error: string | null;
    recordedInWms: boolean;
}> = ({ dispatch, onClose, onSubmit, busy, error, recordedInWms }) => {
    const [form, setForm] = useState<DispatchForm>({ transporterName: '', trackingNumber: '', dispatchDate: todayIst(), invoiceNumber: '', invoiceDate: todayIst() });
    const [files, setFiles] = useState<Array<{ kind: DispatchDocument['kind']; file: File | null }>>([{ kind: 'TAX_INVOICE', file: null }, { kind: 'LR_COPY', file: null }]);
    const set = (patch: Partial<DispatchForm>) => setForm((f) => ({ ...f, ...patch }));
    const ready = form.transporterName.trim() && form.trackingNumber.trim() && form.invoiceNumber.trim() && form.dispatchDate && form.invoiceDate;

    return (
        <Modal opened onClose={onClose} title={`Mark ${dispatch.code} as dispatched`} centered size="lg">
            <div className={styles.stack}>
                <div className={styles.fieldGrid}>
                    <TextInput label="Transporter" required value={form.transporterName} onChange={(e) => set({ transporterName: e.currentTarget.value })} />
                    <TextInput label="LR / AWB number" required value={form.trackingNumber} onChange={(e) => set({ trackingNumber: e.currentTarget.value })} />
                    <TextInput label="Dispatch date" type="date" required value={form.dispatchDate} onChange={(e) => set({ dispatchDate: e.currentTarget.value })} />
                    <TextInput label="E-way bill number" value={form.ewayBillNumber ?? ''} onChange={(e) => set({ ewayBillNumber: e.currentTarget.value || undefined })} />
                    <TextInput label="Invoice number" required value={form.invoiceNumber} onChange={(e) => set({ invoiceNumber: e.currentTarget.value })} />
                    <TextInput label="Invoice date" type="date" required value={form.invoiceDate} onChange={(e) => set({ invoiceDate: e.currentTarget.value })} />
                    <NumberInput label="Invoice amount" min={0} decimalScale={2} value={form.invoiceAmount ?? ''} onChange={(v) => set({ invoiceAmount: v === '' ? undefined : Number(v) })} />
                </div>
                {files.map((entry, index) => (
                    <div key={index} className={styles.row}>
                        <Select size="xs" data={DOC_KINDS} value={entry.kind} onChange={(v) => setFiles((all) => all.map((f, i) => (i === index ? { ...f, kind: (v as DispatchDocument['kind']) ?? 'OTHER' } : f)))} w={180} />
                        <FileInput
                            size="xs"
                            placeholder="PDF, JPG or PNG, up to 10 MB"
                            accept="application/pdf,image/jpeg,image/png,image/webp"
                            value={entry.file}
                            onChange={(file) => setFiles((all) => all.map((f, i) => (i === index ? { ...f, file } : f)))}
                            flex={1}
                        />
                    </div>
                ))}
                <Button size="xs" variant={ButtonVariant.SUBTLE} w="fit-content" onClick={() => setFiles((all) => [...all, { kind: 'OTHER', file: null }])}>
                    Add another document
                </Button>
                <span className={styles.muted}>
                    {recordedInWms
                        ? 'Saving emails the customer that the goods are on the way. Stock is not booked out here: your warehouse system records this dispatch and its sync books it.'
                        : 'Saving books the stock out of the ship-from warehouse and emails the customer that the goods are on the way.'}
                </span>
                {error ? <span className={styles.error}>{error}</span> : null}
                <Button
                    variant={ButtonVariant.ACTION}
                    disabled={!ready}
                    loading={busy}
                    onClick={() => void onSubmit(form, files.filter((f): f is { kind: DispatchDocument['kind']; file: File } => !!f.file))}
                >
                    Mark dispatched
                </Button>
            </div>
        </Modal>
    );
};

/** STN status as the brand reads it. */
const STN_LABEL: Record<B2bDispatch['status'], { label: string; tone: 'info' | 'positive' | 'muted' }> = {
    DRAFT: { label: 'STN created', tone: 'info' },
    APPROVED: { label: 'STN created', tone: 'info' },
    DISPATCHED: { label: 'Dispatched', tone: 'positive' },
    IN_TRANSIT: { label: 'Dispatched', tone: 'positive' },
    RECEIVED: { label: 'Received', tone: 'positive' },
    CANCELLED: { label: 'Cancelled', tone: 'muted' },
};
const LEFT_THE_WAREHOUSE = new Set<B2bDispatch['status']>(['DISPATCHED', 'IN_TRANSIT', 'RECEIVED']);

interface PlanRow {
    productId: string;
    name: string;
    ordered: number;
    onStn: number;
    dispatched: number;
    total?: boolean;
}

/**
 * Creating an STN: where it ships from and how many units of each product still without an STN
 * go on it (pre-filled with everything left; lower a line to split the dispatch).
 */
const CreateStnModal: React.FC<{
    rows: Array<{ productId: string; name: string; left: number }>;
    warehouses: Array<{ value: string; label: string }>;
    defaultShipFrom: string | null;
    available: Map<string, number>;
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onCreate: (fromWarehouseId: string, lines: Array<{ productId: string; quantity: number }>) => void;
}> = ({ rows, warehouses, defaultShipFrom, available, busy, error, onClose, onCreate }) => {
    const [shipFrom, setShipFrom] = useState<string | null>(defaultShipFrom);
    const [qty, setQty] = useState<Record<string, number>>(Object.fromEntries(rows.map((r) => [r.productId, r.left])));
    // Stock is known for the order's usual ship-from warehouse only.
    const showStock = !!shipFrom && shipFrom === defaultShipFrom;
    const lines = Object.entries(qty)
        .filter(([, q]) => q > 0)
        .map(([productId, quantity]) => ({ productId, quantity }));

    return (
        <Modal opened onClose={onClose} title="Create STN" centered size="lg">
            <div className={styles.stack}>
                <Select label="Ship from" data={warehouses} value={shipFrom} onChange={setShipFrom} searchable comboboxProps={{ position: 'bottom-start', width: 'target' }} />
                <div className={styles.stnGrid}>
                    <span className={styles.muted}>Product</span>
                    <span className={styles.muted}>No STN yet</span>
                    <span className={styles.muted}>{showStock ? 'Available' : ''}</span>
                    <span className={styles.muted}>Units on this STN</span>
                    {rows.map((r) => (
                        <React.Fragment key={r.productId}>
                            <span className={styles.fieldValue}>{r.name}</span>
                            <span>{units(r.left)}</span>
                            <span className={showStock && (available.get(r.productId) ?? 0) < (qty[r.productId] ?? 0) ? styles.error : undefined}>
                                {showStock ? units(available.get(r.productId) ?? 0) : ''}
                            </span>
                            <NumberInput
                                size="xs"
                                min={0}
                                max={r.left}
                                allowDecimal={false}
                                value={qty[r.productId] ?? 0}
                                onChange={(v) => setQty((q) => ({ ...q, [r.productId]: Math.min(Number(v) || 0, r.left) }))}
                            />
                        </React.Fragment>
                    ))}
                </div>
                <span className={styles.muted}>Lower a line to send part now and the rest on a later STN.</span>
                {error ? <span className={styles.error}>{error}</span> : null}
                <div className={styles.rowEnd}>
                    <Button variant={ButtonVariant.DEFAULT} disabled={busy} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant={ButtonVariant.ACTION} loading={busy} disabled={!shipFrom || lines.length === 0} onClick={() => shipFrom && onCreate(shipFrom, lines)}>
                        Create STN
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

/**
 * Dispatching a paid B2B order. The table is status only: per product, ordered, on an STN, and
 * dispatched. "Create STN" raises one stock transfer per physical shipment (several are normal);
 * each STN is then marked dispatched with its paperwork (transporter, LR/AWB, invoice), which
 * books the stock out.
 */
const DispatchTab: React.FC<{ detail: B2bOrderDetail; names: Map<string, string>; defaultShipFrom: string | null; isAdmin: boolean; onChanged: () => void }> = ({
    detail,
    names,
    defaultShipFrom,
    isAdmin,
    onChanged,
}) => {
    const { order, dispatches } = detail;
    const canDispatch = order.status === 'Payment Done';
    const { data: warehouses = [] } = useQuery({ queryKey: ['warehouses-picker-internal'], queryFn: listWarehousesForPicker, staleTime: 5 * 60 * 1000 });
    const { data: config } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig, staleTime: 5 * 60 * 1000, retry: false });
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [recording, setRecording] = useState<B2bDispatch | null>(null);
    const [closing, setClosing] = useState(false);
    const [closeReason, setCloseReason] = useState('');

    const warehouseName = useMemo(() => new Map(warehouses.map((w) => [w.warehouseId, w.name])), [warehouses]);
    const nothingLeft = dispatches.balance.every((b) => b.remaining <= 0);
    const dispatchedOf = useMemo(() => {
        const out = new Map<string, number>();
        for (const d of dispatches.dispatches.filter((x) => LEFT_THE_WAREHOUSE.has(x.status))) {
            for (const i of d.items) out.set(i.itemId, (out.get(i.itemId) ?? 0) + Number(i.dispatchedQty ?? i.plannedQty));
        }
        return out;
    }, [dispatches.dispatches]);

    const productRows: PlanRow[] = dispatches.balance.map((b) => ({
        productId: b.productId,
        name: names.get(b.productId) ?? b.productId,
        ordered: b.ordered,
        onStn: b.onDispatches,
        dispatched: dispatchedOf.get(b.productId) ?? 0,
    }));
    const sum = (k: 'ordered' | 'onStn' | 'dispatched') => productRows.reduce((t, r) => t + r[k], 0);
    const rows: PlanRow[] = productRows.length > 1 ? [...productRows, { productId: '__total', name: 'Total', ordered: sum('ordered'), onStn: sum('onStn'), dispatched: sum('dispatched'), total: true }] : productRows;

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        try {
            await action();
            onChanged();
            return true;
        } catch (e) {
            setError(errorMessage(e));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const bold = (r: PlanRow, text: string) => (r.total ? <strong>{text}</strong> : text);
    const columns: DataSheetColumn<PlanRow>[] = [
        { key: 'name', header: 'Product', width: 320, render: (v: string, r) => bold(r, v) },
        { key: 'ordered', header: 'Ordered', width: 110, align: 'right', render: (v: number, r) => bold(r, units(v)) },
        { key: 'onStn', header: 'STN created', width: 120, align: 'right', render: (v: number, r) => bold(r, units(v)) },
        { key: 'dispatched', header: 'Dispatched', width: 120, align: 'right', render: (v: number, r) => bold(r, units(v)) },
    ];

    const lineColumns: DataSheetColumn<B2bDispatch['items'][number]>[] = [
        { key: 'itemId', header: 'Product', width: 280, render: (v: string) => names.get(v) ?? v },
        { key: 'plannedQty', header: 'Units', width: 90, align: 'right', render: (v: number) => units(v) },
        { key: 'receivedQty', header: 'Received', width: 100, align: 'right', render: (v: number | null) => units(v) },
    ];

    return (
        <div className={styles.panelBody}>
            <Card
                title="Plan dispatch"
                actions={
                    <div className={styles.row}>
                        {isAdmin && canDispatch && dispatches.dispatches.some((d) => d.status !== 'CANCELLED') && !nothingLeft ? (
                            <Button className={styles.compactButton} variant={ButtonVariant.OUTLINED_DESTRUCTIVE} onClick={() => setClosing(true)}>
                                Close short
                            </Button>
                        ) : null}
                        {canDispatch && !nothingLeft ? (
                            <Button className={styles.compactButton} variant={ButtonVariant.ACTION} onClick={() => setCreating(true)}>
                                Create STN
                            </Button>
                        ) : null}
                    </div>
                }
            >
                <DataSheet<PlanRow> bare hideHeaderBar data={rows} columns={columns} getRowKey={(r) => r.productId} maxHeight="auto" />
                {!canDispatch && !nothingLeft ? <span className={styles.muted}>STNs can be created once payment is done (now {order.status}).</span> : null}
                {nothingLeft ? <span className={styles.muted}>Every unit is on an STN.</span> : null}
                {error && !creating ? <span className={styles.error}>{error}</span> : null}
            </Card>

            {dispatches.dispatches.map((d) => (
                <Card
                    key={d.id}
                    title={d.code}
                    actions={
                        <div className={styles.row}>
                            <Pill label={STN_LABEL[d.status].label} tone={STN_LABEL[d.status].tone} />
                            {d.dispatchDetails ? (
                                <Button className={styles.compactButton} variant={ButtonVariant.ACTION} leftSection={<FileXlsIcon size={14} />} onClick={() => void run(() => openSignedFile(() => getB2bPackingListUrl(order.purchaseOrderId, d.id)))}>
                                    Download packing list
                                </Button>
                            ) : null}
                            {d.status === 'APPROVED' ? (
                                <>
                                    <Button className={styles.compactButton} variant={ButtonVariant.DEFAULT} loading={busy} onClick={() => void run(() => cancelB2bDispatch(order.purchaseOrderId, d.id))}>
                                        Cancel STN
                                    </Button>
                                    <Button className={styles.compactButton} variant={ButtonVariant.ACTION} onClick={() => setRecording(d)}>
                                        Mark dispatched
                                    </Button>
                                </>
                            ) : null}
                        </div>
                    }
                >
                    <div className={styles.fieldGrid}>
                        <Field label="From" value={d.fromNodeName ?? (d.fromNodeId ? warehouseName.get(d.fromNodeId) ?? d.fromNodeId : '—')} />
                        <Field label="Dispatched" value={formatDay(d.dispatchedAt)} />
                        {d.dispatchDetails ? (
                            <>
                                <Field label="Transporter" value={d.dispatchDetails.transporterName} />
                                <Field label="LR / AWB" value={d.dispatchDetails.trackingNumber} />
                                <Field label="Invoice" value={`${d.dispatchDetails.invoiceNumber} · ${formatDay(d.dispatchDetails.invoiceDate)}`} />
                                <Field label="E-way bill" value={d.dispatchDetails.ewayBillNumber ?? '—'} />
                            </>
                        ) : null}
                        <Field label="Received by customer" value={d.receivedAt ? formatDay(d.receivedAt) : '—'} />
                    </div>
                    <DataSheet bare hideHeaderBar data={d.items} columns={lineColumns} getRowKey={(r) => r.id} maxHeight="auto" />
                    {d.dispatchDetails?.documents.length ? (
                        <div className={styles.row}>
                            {d.dispatchDetails.documents.map((doc) => (
                                <Button key={doc.fileKey} size="xs" variant={ButtonVariant.SUBTLE} onClick={() => void openSignedFile(() => getB2bOrderFileUrl(order.purchaseOrderId, doc.fileKey))}>
                                    {DOC_KINDS.find((k) => k.value === doc.kind)?.label ?? doc.kind}: {doc.fileName}
                                </Button>
                            ))}
                        </div>
                    ) : null}
                </Card>
            ))}

            {creating ? (
                <CreateStnModal
                    rows={productRows.filter((r) => r.ordered - r.onStn > 0).map((r) => ({ productId: r.productId, name: r.name, left: r.ordered - r.onStn }))}
                    warehouses={warehouses.map((w) => ({ value: w.warehouseId, label: w.name }))}
                    defaultShipFrom={defaultShipFrom}
                    available={new Map(detail.availability.map((a) => [a.productId, a.available]))}
                    busy={busy}
                    error={error}
                    onClose={() => setCreating(false)}
                    onCreate={(fromWarehouseId, lines) =>
                        void run(() => createB2bDispatch(order.purchaseOrderId, { fromWarehouseId, lines })).then((ok) => ok && setCreating(false))
                    }
                />
            ) : null}

            {recording ? (
                <RecordDispatchModal
                    dispatch={recording}
                    onClose={() => setRecording(null)}
                    onSubmit={async (form, files) => {
                        const ok = await run(() => recordB2bDispatch(order.purchaseOrderId, recording.id, form, files));
                        if (ok) setRecording(null);
                    }}
                    busy={busy}
                    error={error}
                    recordedInWms={!!config?.dispatchRecordedInWms}
                />
            ) : null}

            <Modal opened={closing} onClose={() => setClosing(false)} title="Close the rest short" centered>
                <div className={styles.stack}>
                    <span className={styles.cardHint}>Every line with units still to dispatch is cancelled. What already left stays on the order.</span>
                    <Textarea label="Reason" autosize minRows={2} value={closeReason} onChange={(e) => setCloseReason(e.currentTarget.value)} />
                    <Button
                        variant={ButtonVariant.FILLED_DESTRUCTIVE}
                        disabled={!closeReason.trim()}
                        loading={busy}
                        onClick={() => void run(() => closeB2bOrderShort(order.purchaseOrderId, closeReason.trim())).then((ok) => ok && setClosing(false))}
                    >
                        Close short
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default DispatchTab;
